<script setup>
import _ from 'lodash'
import { ref, onMounted, computed, watch, getCurrentInstance } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import IconBase from '../icon/IconBase.vue'
import IconExternalLink from '../icon/IconExternalLink.vue'
import IconChevronDown from '../icon/IconChevronDown.vue'
import IconChevronUpDown from '../icon/IconChevronUpDown.vue'
import FormButton from '../Atoms/FormButton.vue'
import FormSwitch from '../Atoms/FormSwitch.vue'
import FormUpload from '../Atoms/FormUpload.vue'
import Alert from '../Atoms/Alert.vue'
import Multiselect from 'vue-multiselect'
import IconInfo from '../icon/IconInfo.vue'
import Tooltip from '../Atoms/Tooltip.vue'
import DropDownMenu from '../Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue'
import ButtonCopyToClipboard from '../Moleculas/ButtonCopyToClipboard.vue'
import Spinner from '../ui/Spinner.vue'
import VODActionBar from './VODActionBar.vue'
import StreamService from '@/services/StreamService'
import { track, EVENTS } from '@/lib/tracking'

const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance()
const root = instance.proxy.$root
const route = useRoute()

const props = defineProps({
  vodFile: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id')
    }
  },
  vodFolder: {
    type: Object,
    required: true,
    validator(v) {
      return Object.prototype.hasOwnProperty.call(v, '_id')
    }
  },
  playerUrl: {
    type: String
  },
  streamMeta: {
    type: Object,
  },
  subTab: {
    type: String,
    default: 'embed' // 'embed' | 'player'
  }
})

const processing = ref(false)
const playerTheme = ref('Default')
const themeDropdown = ref(null)

// ─── Watermark state ──────────────────────────────────────────────────────
const watermark = ref({
  error: false,
  enabled: false,
  value: null,
  valueType: 'string'
})
const watermarkTemp = ref(null)
const watermarkPosition = ref({
  value: 'tr',
})
const watermarkPositionDropdown = ref(null)
const isLoadingWatermark = ref(false)

const positionMap = {
  tr: 'Top Right',
  tl: 'Top Left',
  cc: 'Center',
  bl: 'Bottom Left',
  br: 'Bottom Right',
}

const watermarkUrl = computed(() => {
  if (watermarkTemp.value) return watermarkTemp.value
  const imageId = watermark.value.value
  return imageId ? `https://assets.castr.io/embedImages/${imageId}` : null
})

const waterMarkPosStr = computed(() => positionMap[watermarkPosition.value.value] || 'Top Right')

const features = ref(
  {
    audioOnly: { status: false },
    embedAutoplay: { status: true },
    responsive: { status: true },
    loop: { status: false },
    muted: { status: false },
    hiddenControl: { status: false },
    hiddenVolume: { status: false },
    playbackSpeed: { status: false },
    allowDownload: { status: false },
    socialSharingEnabled: { status: false },
    showTitle: { status: false },
    playbackSpeedOptions: ['0.25', '0.5', '1', '1.25', '1.5', '2', '4'],
    hideOverlayPlayButton: { status: false },
    // per-platform social share toggles
    shareFacebook: { status: true },
    shareTwitter: { status: true },
    shareLinkedIn: { status: false },
    shareReddit: { status: false },
    shareEmail: { status: false },
    shareWhatsApp: { status: false },
  }
)

const socialPlatforms = [
  { key: 'shareFacebook', label: 'Facebook' },
  { key: 'shareTwitter', label: 'X (Twitter)' },
  { key: 'shareLinkedIn', label: 'LinkedIn' },
  { key: 'shareReddit', label: 'Reddit' },
  { key: 'shareEmail', label: 'Email' },
  { key: 'shareWhatsApp', label: 'WhatsApp' },
]

const socialProcessing = ref({
  shareFacebook: false,
  shareTwitter: false,
  shareLinkedIn: false,
  shareReddit: false,
  shareEmail: false,
  shareWhatsApp: false,
})

const emit = defineEmits({
  'update:meta': (value) => { return value },
  'update:playbackUrl': (value) => { return value }
})

const getHlsLink = computed(() => {
  const user = store.state.User
  if (user.hlsForVod && user.hlsUrlForVod && user.hlsUrlForVod !== 'n/a') {
    return `https://${user.hlsUrlForVod}/videos/${props.vodFolder?.key}/${props.vodFile?.id}/index.m3u8`
  }
  const host = "singlemusic-vod.akamaized.net"
  return `https://${host}/encoded/${props.vodFolder?.key}/${props.vodFile?.id}/index.m3u8`
})

const allowVODDownloads = computed(() => store.state.User.vodPlayerDownload ?? false)

const isPlaybackUrlAllowed = computed(() => {
  const subs = store.state.User.subscriptions || []
  const active = subs.filter(x => x.enabled && ['vod', 'live'].includes(x.category))
  return active.some((sub) => {
    if (sub.definitionOverride && !_.isNil(sub.definitionOverride.mp4PlaybackUrl)) {
      return sub.definitionOverride.mp4PlaybackUrl
    } else if (sub.package && !_.isNil(sub.package.definition.mp4PlaybackUrl)) {
      return sub.package.definition.mp4PlaybackUrl
    }
    return false
  })
})

const mp4PlaybackUrl = computed(() => {
  const fileId = props.vodFile?.replaceDetails?.videoId || props.vodFile?.id
  return `https://video-files.castr.net/${props.vodFolder?.key}/${fileId}`
})
const hasHlsLink = computed(() => !!store.state.User?.hlsForVod && !props.vodFolder?.liveParent)

const playbackUrl = computed(() => {
  const iframeSrc = props.playerUrl
  let q = ''
  if (features.value.audioOnly.status) q += '&onlyAudio=true'
  if (features.value.loop.status) q += '&loop=on'
  if (features.value.muted.status) q += '&muted=on'
  if (features.value.hiddenControl.status) q += '&controls=off'
  if (features.value.hiddenVolume.status) q += '&hideSidebarVolume=true'
  if (features.value.allowDownload.status) q += '&allowDownload=true'
  if (q.length && !props.vodFolder.liveParent) q = '?' + q.substring(1)
  return iframeSrc + q
})

const getEpisodeIframeSnippet = computed(() => `<iframe src="${playbackUrl.value}" ${features.value.responsive.status ? 'width="100%" style="aspect-ratio: 16/9; min-height: 340px;"' : 'width="590" height="431"'} frameborder="0" scrolling="no" allow="${features.value.embedAutoplay.status ? 'autoplay; ' : ''}encrypted-media" allowfullscreen  webkitallowfullscreen mozallowfullscreen oallowfullscreen msallowfullscreen></iframe>`)

onMounted(() => getMeta())
watch(() => props.streamMeta, () => getMeta())

const getMeta = () => {
  if (props.streamMeta) {
    features.value.embedAutoplay.status = props.streamMeta?.embedAutoplay
    features.value.hideOverlayPlayButton.status = props.streamMeta?.hideOverlayPlayButton
  }
  if (props.streamMeta?.videoMeta && !_.isEmpty(props.streamMeta?.videoMeta ?? [])) {
    const vm = props.streamMeta.videoMeta
    features.value.playbackSpeed.status = vm.playbackSpeed || false
    features.value.playbackSpeedOptions = vm.playbackSpeedOptions || ['0.25', '0.5', '1', '1.25', '1.5', '2', '4']
    features.value.embedAutoplay.status = vm.embedAutoplay
    features.value.hideOverlayPlayButton.status = vm.hideOverlayPlayButton
    features.value.allowDownload.status = vm.allowDownload || false
    features.value.showTitle.status = vm.showTitle || false
    features.value.socialSharingEnabled.status = vm.socialSharingEnabled || false
    // per-platform toggles — match VODSocialTab defaults (Facebook + Twitter default to true)
    features.value.shareFacebook.status = vm.shareFacebook === undefined ? true : vm.shareFacebook
    features.value.shareTwitter.status = vm.shareTwitter === undefined ? true : vm.shareTwitter
    features.value.shareLinkedIn.status = vm.shareLinkedIn || false
    features.value.shareReddit.status = vm.shareReddit || false
    features.value.shareEmail.status = vm.shareEmail || false
    features.value.shareWhatsApp.status = vm.shareWhatsApp || false
    // watermark
    if (vm.embedWatermark) watermark.value.value = vm.embedWatermark
  }
  if (props.streamMeta?.embedWatermarkPosition) {
    watermarkPosition.value.value = props.streamMeta.embedWatermarkPosition
  }
}

const toggleFeature = async (featureName) => {
  try {
    await StreamServiceSave(featureName, features.value[featureName].status)
    emit('update:meta')
    notify({ group: 'success', text: 'Setting saved' })
  } catch (e) {
    console.log(e)
    notify({ group: 'error', text: 'Unable to save your changes.' })
  }
}

// Use the value emitted by the switch directly to avoid saving a stale state
// value (FormSwitch only emits update:modelValue, not a native change event).
const onTogglePlayerFeature = async (t, value) => {
  features.value[t.key].status = value
  if (t.save) await toggleFeature(t.key)
}

const StreamServiceSave = async (key, value) => {
  await StreamService.saveStreamMetadata(route.params.streamId, key, value, props.vodFile._id)
  StreamService.clearStreamMetadataCache(route.params.streamId)
}

const clipboardCopy = async (value) => {
  await navigator.clipboard.writeText(value)
  notify({ group: 'info', text: 'Copied to clipboard' })
}

const trackEmbedCopied = () => {
  track(EVENTS.EMBED_CODE_COPIED, {
    contentType: 'vod',
    copyType: 'embed_code',
    folderId: props.vodFolder?._id,
    fileId: props.vodFile?._id,
  })
}

const trackUrlCopied = (copyType) => {
  track(EVENTS.CONTENT_URL_COPIED, {
    contentType: 'vod',
    copyType,
    folderId: props.vodFolder?._id,
    fileId: props.vodFile?._id,
  })
}

const sanitizePlaybackSpeed = (speed) => {
  let num = Number(speed)
  if (isNaN(num)) return false
  if (num < 0.25 || num > 4) return false
  let digits = 2
  const [, decimalPlaces] = num?.toFixed(2)?.split('.')
  if (Number(decimalPlaces) < 1) digits = 0
  else if (decimalPlaces[1] === '0') digits = 1
  return num.toFixed(digits)
}

const addPlaybackSpeed = (newSpeed) => {
  const sanitized = sanitizePlaybackSpeed(newSpeed)
  if (!sanitized) return notify({ group: 'error', text: 'Invalid playback speed' })
  if (features.value.playbackSpeedOptions.includes(sanitized)) return notify({ group: 'error', text: 'Duplicate playback speed' })
  features.value.playbackSpeedOptions.unshift(sanitized)
  updatePlaybackSpeeds()
}

const updatePlaybackSpeeds = async () => {
  try {
    await StreamService.saveStreamMetadata(route.params.streamId, 'playbackSpeedOptions', features.value.playbackSpeedOptions, props.vodFile._id)
    StreamService.clearStreamMetadataCache(route.params.streamId)
    emit('update:meta')
  } catch (e) {
    console.log(e)
    notify({ group: 'error', text: 'Unable to save your changes.' })
  }
}

const toggleSocialPlatform = async (key, value) => {
  features.value[key].status = value
  socialProcessing.value[key] = true
  try {
    await StreamService.saveStreamMetadata(route.params.streamId, key, value, props.vodFile._id)
    StreamService.clearStreamMetadataCache(route.params.streamId)
    notify({ group: 'success', text: 'Setting saved' })
  } catch (e) {
    console.log(e)
    notify({ group: 'error', text: 'Unable to save your changes.' })
  }
  socialProcessing.value[key] = false
}

const changeTheme = (value) => {
  playerTheme.value = value
  themeDropdown.value?.toggleDropdown()
}

// ─── Watermark handlers ───────────────────────────────────────────────────
function imageReader(file, cb) {
  if (!file) return
  const reader = new FileReader()
  reader.onload = function () {
    if (cb) cb(reader.result)
  }
  reader.readAsDataURL(file)
}

const onWatermarkPreview = (file) => {
  const imageInput = document.querySelector('#watermark-player-input')
  if (!file) return

  if (file.type.indexOf('image/') === -1) {
    notify({ group: 'error', text: 'Only image files are allowed' })
    if (imageInput) imageInput.value = null
    return
  }

  const maxSize = 1 * 1024 * 1024
  if (file.size > maxSize) {
    notify({ group: 'error', text: 'Watermark image must be under 1 MB' })
    if (imageInput) imageInput.value = null
    return
  }

  imageReader(file, (base64) => {
    watermarkTemp.value = base64
    watermark.value.error = false
  })
}

const saveWatermarkPosition = async () => {
  const newpos = watermarkPosition.value.value
  await StreamService.saveStreamMetadata(route.params.streamId, 'embedWatermarkPosition', newpos)
  StreamService.clearStreamMetadataCache(route.params.streamId)
}

const setWatermarkPosition = (value) => {
  watermarkPosition.value.value = value
  saveWatermarkPosition()
  watermarkPositionDropdown.value?.toggleDropdown()
}

const saveWatermark = async () => {
  watermark.value.error = false
  const imageInput = document.querySelector('#watermark-player-input')
  const imageFile = imageInput?.files[0]

  if (!imageInput?.value || !imageFile) {
    watermark.value.error = 'Please pick an image file'
    return
  }

  isLoadingWatermark.value = true

  const fdata = new FormData()
  fdata.append('file', imageFile)

  const res = await StreamService.uploadStreamPoster(
    props.vodFolder._id,
    fdata,
    true,
    props.vodFile._id,
    true
  )

  if (res.success) {
    watermark.value.value = res.uploadId
    watermarkTemp.value = null
    await saveWatermarkPosition()
    notify({ group: 'success', text: 'Watermark uploaded successfully' })
  } else {
    watermark.value.error = 'Could not upload watermark. Please try again later.'
  }

  isLoadingWatermark.value = false
}

const cancelWatermarkUpload = async () => {
  watermarkTemp.value = null
  watermark.value.error = false
  watermark.value.value = null
  const el = document.querySelector('#watermark-player-input')
  if (el) el.value = null
  try {
    await StreamService.saveStreamMetadata(route.params.streamId, 'watermark', null, props.vodFile._id)
    StreamService.clearStreamMetadataCache(route.params.streamId)
    notify({ group: 'success', text: 'Watermark removed' })
  } catch (e) {
    console.error(e)
  }
}

watch(playbackUrl, (newUrl) => emit('update:playbackUrl', newUrl), { immediate: true })

// Header copy adapts to the active sub-tab (Embed vs Player).
const panelHeading = computed(() => props.subTab === 'player' ? 'Player settings' : 'Embed & share')
const panelSubheading = computed(() => props.subTab === 'player'
  ? 'Control how the player looks and behaves for your viewers.'
  : 'Copy the embed code into your website, or share the direct link anywhere.')

// Player toggles grouped into labeled panels (enterprise-style sections).
const playerSections = computed(() => {
  const playback = [
    { key: 'embedAutoplay', title: 'Autoplay', desc: 'Start playback automatically when the page loads. Pair with “Start muted” — most browsers block autoplay with sound.', save: true },
    { key: 'loop', title: 'Loop', desc: 'Replay the video automatically when it ends. Ideal for short clips and background loops.' },
    { key: 'muted', title: 'Start muted', desc: 'Load with sound off. Viewers can unmute anytime — recommended alongside Autoplay.' },
  ]
  if (props.vodFolder?.liveParent) {
    playback.push({ key: 'audioOnly', title: 'Audio only', desc: 'Play only the audio track of this recording.' })
  }

  const controls = [
    { key: 'hiddenControl', title: 'Hide player controls', desc: 'Remove the control bar entirely. Viewers can’t pause, seek, or adjust volume.' },
    { key: 'hideOverlayPlayButton', title: 'Hide center play button', desc: 'Hide the large play button over the video. The control-bar play button stays available.', save: true },
    { key: 'hiddenVolume', title: 'Hide volume control', desc: 'Remove the volume button so viewers can’t adjust or mute sound from the player.' },
    { key: 'playbackSpeed', title: 'Playback speed', desc: 'Let viewers change the playback rate (0.5×, 1×, 2×). Great for tutorials and long-form content.', save: true, speeds: true },
  ]

  const sharing = []
  if (allowVODDownloads.value) {
    sharing.push({ key: 'allowDownload', title: 'Allow downloads', desc: 'Add a download button to the player. Each download counts toward your bandwidth usage.', save: true })
  }
  sharing.push({ key: 'socialSharingEnabled', title: 'Social sharing', desc: 'Let viewers share the video and choose which platforms appear.', save: true })

  return [
    { title: 'Playback', items: playback },
    { title: 'Controls', items: controls },
    { title: 'Sharing & downloads', items: sharing },
  ]
})
</script>

<template>
  <section>
    <h3 class="text-[15px] font-medium !mb-1">{{ panelHeading }}</h3>
    <p class="text-surface-8 text-xs !mb-4">{{ panelSubheading }}</p>
    <Spinner v-if="processing" text="Retrieving data..." classes="text-surface-8 my-8" spinner-color="var(--c-dark-8)"
      spinner-size="15px" />

    <!-- ============ EMBED TAB ============ -->
    <div v-else-if="subTab === 'embed'" class="flex flex-col gap-4">
      <!-- Group 1: player embed + shareable link — surface-1 inset -->
      <div class="flex flex-col gap-4 bg-surface-1 rounded-lg p-3">
        <!-- Embed Code -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <label class="text-sm font-medium text-surface-text-1">Embed code</label>
            <FormSwitch size="sm" v-model="features.responsive.status" label="Responsive" />
          </div>
          <div class="relative">
            <!-- Selectable block: users can highlight/copy manually, or use the
                 copy button. Auto-sizes so the whole snippet is always visible. -->
            <div class="bg-surface-3 w-full rounded-md p-2.5 pr-10 text-tiny text-surface-text-1 leading-relaxed break-all whitespace-pre-wrap font-mono cursor-text select-text"
              >{{ getEpisodeIframeSnippet }}</div>
            <div class="absolute top-1.5 right-1.5">
              <ButtonCopyToClipboard :text="getEpisodeIframeSnippet" @click="trackEmbedCopied" />
            </div>
          </div>
        </div>

        <!-- Direct Link (player page) -->
        <div>
          <label class="text-sm font-medium text-surface-text-1 block mb-2">Direct link</label>
          <div class="relative">
            <input :value="playbackUrl" readonly
              class="w-full bg-surface-3 rounded-md p-2.5 pr-16 text-tiny text-surface-text-1 focus:outline-none cursor-text" />
            <div class="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
              <ButtonCopyToClipboard :text="playbackUrl" @click="trackUrlCopied('direct')" />
              <a :href="playbackUrl" target="_blank"
                class="inline-flex items-center justify-center size-7 text-surface-text-1 hover:text-white hover:no-underline rounded-md">
                <icon-base class="shrink-0" fill="transparent">
                  <icon-external-link />
                </icon-base>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Group 2: direct playback URLs (MP4 + HLS) for custom players -->
      <div class="flex flex-col gap-4 bg-surface-1 rounded-lg p-3">
        <div>
          <label class="text-sm font-medium text-surface-text-1 block">Direct playback</label>
          <p class="text-xs text-surface-8 mt-0.5">Play this video in your own player, without our embed.</p>
        </div>

        <!-- MP4 file URL (top) -->
        <div>
          <label class="text-xs font-medium text-surface-8 block mb-1.5">MP4 file URL</label>
          <div v-if="!vodFolder.liveParent && isPlaybackUrlAllowed" class="relative">
            <input :value="mp4PlaybackUrl" readonly
              class="w-full bg-surface-3 rounded-md p-2.5 pr-10 text-tiny text-surface-text-1 focus:outline-none cursor-text" />
            <div class="absolute right-1.5 top-1/2 -translate-y-1/2">
              <ButtonCopyToClipboard :text="mp4PlaybackUrl" @click="trackUrlCopied('mp4')" />
            </div>
          </div>
          <div v-else
            class="bg-surface-3 rounded-md p-2.5 text-tiny text-surface-7 flex items-center justify-between gap-2">
            <span>Available on Ultra plan</span>
            <a class="underline text-main" target="_blank" href="/app/subscribe">Upgrade</a>
          </div>
        </div>

        <!-- HLS URL (bottom) — true VOD only (not for Live-to-VOD archives) -->
        <div v-if="!vodFolder?.liveParent">
          <label class="text-xs font-medium text-surface-8 block mb-1.5">HLS URL</label>
          <div v-if="hasHlsLink" class="relative">
            <input :value="getHlsLink" readonly
              class="w-full bg-surface-3 rounded-md p-2.5 pr-10 text-tiny text-surface-text-1 focus:outline-none cursor-text" />
            <div class="absolute right-1.5 top-1/2 -translate-y-1/2">
              <ButtonCopyToClipboard :text="getHlsLink" @click="trackUrlCopied('hls')" />
            </div>
          </div>
          <div v-else
            class="bg-surface-3 rounded-md p-2.5 text-tiny text-surface-7 flex items-center justify-between gap-2">
            <span>Available on request</span>
            <FormButton :custom-classes="'underline text-main'" onclick="Intercom('show')">Contact support</FormButton>
          </div>
        </div>
      </div>

      <!-- Group 3: manage this video (replace / download / delete) -->
      <div class="flex flex-col gap-3 bg-surface-1 rounded-lg p-3">
        <label class="text-sm font-medium text-surface-text-1 block">Manage video</label>
        <VODActionBar :vodFile="vodFile" :vodFolder="vodFolder" :playbackUrl="playbackUrl" />
      </div>
    </div>

    <!-- ============ PLAYER TAB ============ -->
    <div v-else class="flex flex-col gap-4">
      <!-- Grouped setting panels -->
      <div v-for="section in playerSections" :key="section.title"
        class="flex flex-col gap-1 bg-surface-1 rounded-lg p-3">
        <label class="text-sm font-medium text-surface-text-1 block mb-1">{{ section.title }}</label>
        <div class="flex flex-col divide-y divide-surface-3">
          <div v-for="t in section.items" :key="t.key" class="py-3 first:pt-0 last:pb-0">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="text-sm text-surface-text-1">{{ t.title }}</p>
                <p class="text-xs text-surface-8 mt-0.5">{{ t.desc }}</p>
              </div>
              <FormSwitch class="shrink-0 mt-0.5" size="sm" :model-value="features[t.key].status"
                @update:model-value="(val) => onTogglePlayerFeature(t, val)" />
            </div>
            <div v-if="t.speeds && features.playbackSpeed.status" class="mt-3">
              <multiselect v-model="features.playbackSpeedOptions" :multiple="true" :taggable="true"
                tag-placeholder="Add this as a new playback speed" placeholder="Playback speeds"
                :options="features.playbackSpeedOptions" class="min-h-[2rem]" @tag="addPlaybackSpeed"
                @select="updatePlaybackSpeeds" @remove="updatePlaybackSpeeds" />
              <div class="flex items-center text-surface-8 mt-1">
                <p class="text-s-xs mr-2">Press Enter to add a speed</p>
                <Tooltip :popperClass="'max-w-[400px] text-s-xs'"
                  content="Playback rates range from 0.25 to 4 and can be incremented by 0.1.">
                  <icon-base class="shrink-0">
                    <IconInfo />
                  </icon-base>
                </Tooltip>
              </div>
            </div>
            <!-- Per-platform share toggles (only under socialSharingEnabled when it's on) -->
            <div v-if="t.key === 'socialSharingEnabled' && features.socialSharingEnabled.status" class="mt-3 pt-3 border-t border-surface-3">
              <p class="text-xs text-surface-8 mb-2">Choose which platforms viewers can share to:</p>
              <ul class="flex flex-wrap gap-x-5 gap-y-2">
                <li v-for="p in socialPlatforms" :key="p.key">
                  <FormSwitch
                    size="sm"
                    :label="p.label"
                    :model-value="features[p.key].status"
                    @update:model-value="(val) => toggleSocialPlatform(p.key, val)"
                  />
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Watermark panel -->
      <div class="flex flex-col gap-3 bg-surface-1 rounded-lg p-3">
        <label class="text-sm font-medium text-surface-text-1 block">Watermark</label>
        <p class="text-xs text-surface-8 -mt-2">Overlay your logo on the player. PNG with transparency works best.</p>
        <div class="flex flex-wrap items-center justify-between gap-x-2 gap-y-2">
          <!-- Left: preview / status -->
          <div>
            <img
              v-if="watermarkUrl"
              :src="watermarkUrl"
              alt="Watermark preview"
              class="h-10 max-w-[100px] rounded object-contain"
            />
            <p v-else class="text-xs text-surface-8 whitespace-nowrap">No file chosen</p>
          </div>
          <!-- Right: position dropdown + upload + action buttons -->
          <div class="flex items-center gap-1 shrink-0">
            <span class="text-[11px] text-surface-8 shrink-0">Position</span>
            <DropDownMenu ref="watermarkPositionDropdown" position="right"
              customClasses="bg-surface-3 hover:bg-surface-4 text-[11px] rounded-md flex items-center justify-between h-8 px-2 gap-1 text-nowrap">
              <template #toggle-button>
                <span>{{ waterMarkPosStr }}</span>
                <icon-base class="!ml-0.5 shrink-0">
                  <icon-chevron-up-down />
                </icon-base>
              </template>
              <template #menu-items>
                <DropDownMenuItem @click="setWatermarkPosition('tr')">Top Right</DropDownMenuItem>
                <DropDownMenuItem @click="setWatermarkPosition('tl')">Top Left</DropDownMenuItem>
                <DropDownMenuItem @click="setWatermarkPosition('cc')">Center</DropDownMenuItem>
                <DropDownMenuItem @click="setWatermarkPosition('bl')">Bottom Left</DropDownMenuItem>
                <DropDownMenuItem @click="setWatermarkPosition('br')">Bottom Right</DropDownMenuItem>
              </template>
            </DropDownMenu>
            <FormUpload
              name="watermark"
              id="watermark-player-input"
              placeholder="No file chosen"
              :label="watermark.value ? 'Replace Watermark' : 'Upload Watermark'"
              type="main"
              size="sm"
              customClasses="inline-flex items-center justify-center gap-1 rounded-md font-medium bg-main hover:bg-main-light text-white h-8 px-2.5 text-[11px] cursor-pointer whitespace-nowrap"
              accept="image/*"
              :on-file-change="onWatermarkPreview"
            />
            <FormButton
              v-if="watermarkTemp"
              :disabled="isLoadingWatermark"
              :custom-classes="'inline-flex items-center rounded-md px-2.5 py-1.5 text-xs font-medium bg-main text-white disabled:opacity-50'"
              @click="saveWatermark"
            >
              {{ isLoadingWatermark ? 'Saving…' : 'Save' }}
            </FormButton>
            <FormButton
              v-if="watermarkTemp || watermark.value"
              :disabled="isLoadingWatermark"
              :custom-classes="'inline-flex items-center rounded-md px-2.5 py-1.5 text-xs font-medium ring-1 ring-surface-4 text-surface-text-1 disabled:opacity-50'"
              @click="cancelWatermarkUpload"
            >
              <span v-if="watermarkTemp && watermark.value">Restore</span>
              <span v-else-if="watermarkTemp">Cancel</span>
              <span v-else>Remove</span>
            </FormButton>
          </div>
        </div>
        <Alert type="error" v-show="watermark.error" :text="watermark.error || ''" class="mt-2" />
      </div>
    </div>
  </section>
</template>

<style scoped></style>
